import React, { FC, useContext } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { BrandName } from 'packages/types/brand-name';
import { useResponsiveType } from '@agria/theme/src/utils';
import { Button } from '@agria/theme/src/components/button';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { Account, Logout } from '@agria/theme/src/components/icon';
import { getQuoteUrlByBrand } from '@agria/theme/src/utils/getByBrand';
import { useHeader } from '@agria/theme/src/context/HeaderContext';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import type { NavigationItem, SecondaryNavigationItem } from '../header/Header';
import tokens from '../header/tokens';

interface MobileNavProps {
  activePage?: NavigationItem | null;
  brand?: BrandName;
  ctaLink?: string;
  ctaText?: string;
  enableMyAccount?: boolean;
  loggedIn?: boolean; // Set to true when user is in my account context
  myAccountLogout?: () => void; // Function to logout user
  navigationItems: NavigationItem[];
  secondaryNavigationItems: SecondaryNavigationItem[];
}

const StyledMobileNavContainer = styled.div<{
  isOpen: boolean;
  $AGRIA146?: boolean;
}>`
  ${({ theme: { layers, colors }, isOpen, $AGRIA146 }) => css`
    display: ${isOpen ? 'block' : 'none'};
    left: 0;
    width: 100%;
    background: ${colors.utilityWhite};
    z-index: ${layers.modalOverlay};

    ${
      $AGRIA146
        ? // Fixed rather than absolute, as the sticky header is a containing block. 80px = 48px logo + 16px padding top/bottom
          css`
            display: ${isOpen ? 'flex' : 'none'};
            flex-direction: column;
            position: fixed;
            inset: 80px 0 0;

            ${StyledMobileNav} {
              flex: 1;
              min-height: 0;
              max-height: none;
              border-top: solid 1px ${colors.utilityBackground};
            }

            ${StyledMobileNavBottomButtons} {
              position: static;
            }
          `
        : css`
            position: absolute;
            top: 112px;
            height: 100%;
          `
    }
  `}
`;

const StyledMobileNav = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    overflow-y: auto;
    max-height: calc(100% - 301px);
    padding: ${space.medium};

    @media (min-width: ${media.medium}) {
      max-height: calc(100% - 317px);
      padding: ${space.large};
    }

    a {
      text-decoration: none;

      &[aria-current] {
        color: ${colors[tokens.hoverColour]};
      }
    }
  `}
`;

const StyledPrimaryNavItems = styled.ul`
  ${({ theme: { space, colors, media } }) => css`
    ${StyledMobileNav}:has(${StyledSecondaryNavItems}) & {
      margin-bottom: ${space.small};
      padding-bottom: ${space.small};
      border-bottom: solid 1px ${colors.brandOne10};
    }

    a + ul,
    li + li {
      margin-top: ${space.small};
    }

    li {
      ${useResponsiveType('headingSmall')};

      ul li {
        ${useResponsiveType('bodyMedium')};

        + li {
          margin-top: ${space.xxSmall};
        }
      }
    }

    @media (min-width: ${media.xSmall}) {
      ${StyledMobileNav}:has(${StyledSecondaryNavItems}) & {
        margin-bottom: ${space.medium};
        padding-bottom: ${space.medium};
      }

      li {
        ${useResponsiveType('headingMedium')};

        + li {
          margin-top: ${space.medium};
        }
      }
    }
  `}
`;

const StyledSecondaryNavItems = styled.ul`
  ${({ theme: { space, colors, media } }) => css`
    li + li {
      margin-top: ${space.xxSmall};
    }
  `}
`;

const StyledMobileNavBottomButtons = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    position: fixed;
    bottom: 0;
    width: 100%;
    border-top: solid 1px ${colors.utilityBackground};
    padding: ${space.medium};
    display: grid;
    gap: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      padding: ${space.large};
    }
  `}
`;

export const MobileNav: FC<MobileNavProps> = ({
  activePage,
  brand,
  ctaLink,
  ctaText,
  enableMyAccount,
  loggedIn,
  myAccountLogout,
  navigationItems,
  secondaryNavigationItems,
}) => {
  const { isMobileNavOpen, toggleMobileNav, setIsMobileNavOpen } = useHeader();
  const { experiments } = useContext(ExperimentsContext);

  return (
    <StyledMobileNavContainer
      isOpen={isMobileNavOpen}
      $AGRIA146={experiments.AGRIA146 === 'variation'}
    >
      <StyledMobileNav>
        {navigationItems && navigationItems.length > 0 && (
          <StyledPrimaryNavItems>
            {navigationItems.map((item, index) => (
              <li key={index}>
                <LinkTo to={item.link.url} onClick={toggleMobileNav}>
                  {item.link.label}
                </LinkTo>
                {item.headerChildItems &&
                  item.headerChildItems.length > 0 &&
                  item.link.url === activePage?.link.url && (
                    <ul>
                      {item.headerChildItems.map((childItem, childIndex) => (
                        <li key={childIndex}>
                          {childItem.navigationChildItems?.length > 0 ? (
                            <ul>
                              {childItem.navigationChildItems
                                .filter((grandChildItem) => grandChildItem.link)
                                .map((grandChildItem, grandChildIndex) => (
                                  <li key={grandChildIndex}>
                                    <LinkTo
                                      to={grandChildItem.link.url}
                                      onClick={() => setIsMobileNavOpen(false)}
                                    >
                                      {grandChildItem.link.label}
                                    </LinkTo>
                                  </li>
                                ))}
                            </ul>
                          ) : (
                            <LinkTo
                              to={childItem.link.url || '#'}
                              onClick={() => setIsMobileNavOpen(false)}
                            >
                              {childItem.link.label || '#'}
                            </LinkTo>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
              </li>
            ))}
          </StyledPrimaryNavItems>
        )}
        {secondaryNavigationItems && secondaryNavigationItems.length > 0 && (
          <StyledSecondaryNavItems>
            {secondaryNavigationItems.map((item, index) => (
              <li key={index}>
                <LinkTo
                  to={item.link.url}
                  onClick={() => setIsMobileNavOpen(false)}
                >
                  {item.link.label}
                </LinkTo>
              </li>
            ))}
          </StyledSecondaryNavItems>
        )}
      </StyledMobileNav>

      <StyledMobileNavBottomButtons>
        {enableMyAccount && loggedIn && (
          <Button
            label="Log out"
            onClick={myAccountLogout}
            variant="secondaryOutline"
            iconRight={<Logout />}
          />
        )}
        {enableMyAccount && !loggedIn && (
          <Button
            url="/my-account/login/"
            label="My Account"
            variant={
              experiments.AGRIA146 === 'variation'
                ? 'secondary'
                : 'secondaryOutline'
            }
            iconLeft={
              experiments.AGRIA146 === 'variation' ? (
                <Account size="small" aria-hidden="true" />
              ) : undefined
            }
            onClick={() => setIsMobileNavOpen(false)}
          />
        )}
        {brand !== 'Service Dogs' && activePage?.ctaOverride && (
          <Button
            url={activePage.ctaOverride.url}
            label={activePage.ctaOverride.label}
            onClick={() => setIsMobileNavOpen(false)}
            arrowRight
          />
        )}
        {experiments.AGRIA146 !== 'variation' &&
          brand !== 'Service Dogs' &&
          !activePage?.ctaOverride && (
            <Button
              url={getQuoteUrlByBrand(brand, ctaLink)}
              label={ctaText || 'Get a quote'}
              onClick={() => setIsMobileNavOpen(false)}
              arrowRight
            />
          )}
      </StyledMobileNavBottomButtons>
    </StyledMobileNavContainer>
  );
};
